import AuthGuard from "../utils/route-guard/AuthGuard";
import ClientGuard from "../utils/route-guard/ClientGuard";
import { MainLayout } from "../layout/MainLayout/MainLayout";
import { ProtectedElement } from "./ProtectedRoute";
import { Loadable } from "../components/Loadable/Loadable";
import { lazy } from "react";
import { FormattedMessage } from "react-intl";
import { Navigate } from "react-router-dom";
import {
  CROSS_SITE_VIEWER,
  VIEWER,
  LOCUS_TENANT,
  SUPER_ADMIN,
  LOCUS_CONFIGURATION_ADMIN,
  CONFIGURATION_ADMIN,
  LOCUS_USER_MANAGER,
  LOCUS_CUSTOMER_USER_MANAGER,
  USER_MANAGER,
  SYSTEM_INTEGRATOR,
  LOCUS_TOOLS_ADMINISTRATOR,
  LOCUS_TOOLS_USER,
} from "../utils/constants/user";
import { AssetManagementRoutes } from "./ChildRoutes/AssetManagementRoutes";
import { PermissionsRoutes } from "./ChildRoutes/PermissionsRoutes";
import { UsersRoutes } from "./ChildRoutes/UsersRoutes";
import { ToolsRoutes } from "./ChildRoutes/ToolsRoutes";
import { HelpRoutes } from "./ChildRoutes/HelpRoutes";
import componentLoader from "../utils/helpers/componentLoader";

const FEATURE_ECHART_DASHBOARDS =
  process.env.REACT_APP_FEATURE_ECHART_DASHBOARDS === "true";

const ClientOverview = Loadable(
  lazy(() => componentLoader(() => import("views/client/ClientOverview"))),
);
const SiteCreate = Loadable(
  lazy(() => componentLoader(() => import("views/site/SiteCreate"))),
);
const SiteOverview = Loadable(
  lazy(() => componentLoader(() => import("views/site/SiteOverview"))),
);
const OverviewDashboard = Loadable(
  lazy(() => componentLoader(() => import("views/site/OverviewDashboard"))),
);
const Dashboard = Loadable(
  lazy(() => componentLoader(() => import("views/analytics/Dashboard"))),
);
const DashboardReportsView = Loadable(
  lazy(() =>
    componentLoader(() => import("views/dashboard/DashboardReportsView")),
  ),
);
const LocusDashboards = Loadable(
  lazy(() => componentLoader(() => import("views/dashboard/LocusDashboards"))),
);
const DashboardTaskMap = Loadable(
  lazy(() => componentLoader(() => import("views/dashboard/DashboardTaskMap"))),
);
const DashboardAssociatePerformance = Loadable(
  lazy(() =>
    componentLoader(
      () => import("views/dashboard/DashboardAssociatePerformance"),
    ),
  ),
);
const DashboardDirectedAssociatePerformance = Loadable(
  lazy(() =>
    componentLoader(
      () => import("views/dashboard/DashboardDirectedAssociatePerformance"),
    ),
  ),
);
const DashboardInduction = Loadable(
  lazy(() =>
    componentLoader(() => import("views/dashboard/DashboardInduction")),
  ),
);
const DashboardOpenWork = Loadable(
  lazy(() =>
    componentLoader(() => import("views/dashboard/DashboardOpenWork")),
  ),
);
const DashboardOverview = Loadable(
  lazy(() =>
    componentLoader(() => import("views/dashboard/DashboardOverview")),
  ),
);
const DashboardWorkAssignment = Loadable(
  lazy(() =>
    componentLoader(() => import("views/dashboard/DashboardWorkAssignment")),
  ),
);
const Unauthorized = Loadable(
  lazy(() =>
    componentLoader(() => import("components/Unauthorized/Unauthorized")),
  ),
);
const ContainerConfiguration = Loadable(
  lazy(() =>
    componentLoader(
      () => import("views/site/container-configuration/ContainerConfiguration"),
    ),
  ),
);

export const ClientRoutes = {
  path: "/client/:clientId",
  element: (
    <AuthGuard>
      <ClientGuard>
        <MainLayout />
      </ClientGuard>
    </AuthGuard>
  ),
  children: [
    {
      path: "unauthorized",
      element: <Unauthorized />,
    },
    {
      path: "overview",
      element: (
        <ProtectedElement
          element={ClientOverview}
          allowedGroups={[
            LOCUS_TENANT,
            CROSS_SITE_VIEWER,
            CONFIGURATION_ADMIN,
            LOCUS_USER_MANAGER,
            LOCUS_CUSTOMER_USER_MANAGER,
            USER_MANAGER,
            SYSTEM_INTEGRATOR,
          ]}
        />
      ),
      children: [],
    },
    {
      path: "create-site",
      breadcrumb: {
        display: "Create Site",
      },
      element: (
        <ProtectedElement
          element={SiteCreate}
          allowedGroups={[SUPER_ADMIN, LOCUS_CONFIGURATION_ADMIN]}
        />
      ),
    },
    {
      path: "site-info",
      element: (
        <ProtectedElement
          element={SiteOverview}
          allowedGroups={[
            LOCUS_TENANT,
            CROSS_SITE_VIEWER,
            VIEWER,
            CONFIGURATION_ADMIN,
            LOCUS_CONFIGURATION_ADMIN,
          ]}
        />
      ),
      breadcrumb: {
        display: "Site Overview",
      },
    },
    {
      path: "overview-dashboard",
      element: (
        <ProtectedElement
          element={OverviewDashboard}
          allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
        />
      ),
      breadcrumb: {
        display: "Site Overview",
      },
    },
    {
      path: "dashboards",
      breadcrumb: {
        display: "Dashboards",
      },
      children: [
        {
          path: "",
          element: (
            <ProtectedElement
              element={DashboardReportsView}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "performance",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "visibility",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "management",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
      ],
    },
    {
      path: "reports",
      breadcrumb: {
        display: "Reports",
      },
      children: [
        {
          path: "",
          element: (
            <ProtectedElement
              element={DashboardReportsView}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "performance",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "visibility",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
        {
          path: "management",
          element: (
            <ProtectedElement
              element={Dashboard}
              allowedGroups={[LOCUS_TENANT, CROSS_SITE_VIEWER, VIEWER]}
            />
          ),
        },
      ],
    },
    {
      path: "container-configuration",
      element: (
        <ProtectedElement
          element={ContainerConfiguration}
          allowedGroups={[
            SUPER_ADMIN,
            LOCUS_TOOLS_ADMINISTRATOR,
            LOCUS_TOOLS_USER,
          ]}
        />
      ),
      breadcrumb: {
        display: "Containers",
      },
    },
    {
      path: "custom-dashboards",
      breadcrumb: {
        display: "Custom Dashboards",
      },
      element: FEATURE_ECHART_DASHBOARDS ? (
        <Navigate to="../dashboards" replace />
      ) : (
        <ProtectedElement
          element={LocusDashboards}
          allowedGroups={[SUPER_ADMIN]}
        />
      ),
    },
    // {
    //   path: "custom-dashboards/taskmap",
    //   breadcrumb: {
    //     display: <FormattedMessage id="taskmap" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardTaskMap}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/associateperformance",
    //   breadcrumb: {
    //     display: <FormattedMessage id="associateperformance" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardAssociatePerformance}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/directedassociateperformance",
    //   breadcrumb: {
    //     display: <FormattedMessage id="directedassociateperformance" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardDirectedAssociatePerformance}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/induction",
    //   breadcrumb: {
    //     display: <FormattedMessage id="induction" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardInduction}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/openwork",
    //   breadcrumb: {
    //     display: <FormattedMessage id="openwork" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardOpenWork}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/overview",
    //   breadcrumb: {
    //     display: <FormattedMessage id="overview" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardOverview}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    // {
    //   path: "custom-dashboards/workassignment",
    //   breadcrumb: {
    //     display: <FormattedMessage id="workassignment" />,
    //   },
    //   element: (
    //     <ProtectedElement
    //       element={DashboardWorkAssignment}
    //       allowedGroups={[SUPER_ADMIN]}
    //     />
    //   ),
    // },
    UsersRoutes,
    PermissionsRoutes,
    AssetManagementRoutes,
    ToolsRoutes,
    HelpRoutes,
  ],
};
